import { create } from "zustand";

interface AgentInfo {
  userId: string;
  firstName: string;
  lastName: string;
  avatarUrl: string | null;
  status: string;
}

interface TypingAgent {
  conversationId: string;
  userId: string;
  name: string;
}

interface TypingCustomer {
  conversationId: string;
  customerId: string;
  name: string;
}

interface PresenceState {
  /** Online agents keyed by userId */
  onlineAgents: Map<string, AgentInfo>;
  /** Typing agents keyed by `${conversationId}:${userId}` */
  typingAgents: Map<string, TypingAgent>;
  /** Typing customers keyed by `${conversationId}:${customerId}` */
  typingCustomers: Map<string, TypingCustomer>;

  setAgentOnline: (userId: string, data: Omit<AgentInfo, "userId">) => void;
  setAgentOffline: (userId: string) => void;
  setAgentTyping: (conversationId: string, userId: string, name: string) => void;
  clearAgentTyping: (conversationId: string, userId: string) => void;
  setCustomerTyping: (conversationId: string, customerId: string, name: string) => void;
  clearCustomerTyping: (conversationId: string, customerId: string) => void;
  reset: () => void;
}

const initialState = {
  onlineAgents: new Map<string, AgentInfo>(),
  typingAgents: new Map<string, TypingAgent>(),
  typingCustomers: new Map<string, TypingCustomer>(),
};

export const usePresenceStore = create<PresenceState>((set) => ({
  ...initialState,

  setAgentOnline: (userId: string, data: Omit<AgentInfo, "userId">) =>
    set((state) => {
      const next = new Map(state.onlineAgents);
      next.set(userId, { userId, ...data });
      return { onlineAgents: next };
    }),

  setAgentOffline: (userId: string) =>
    set((state) => {
      const next = new Map(state.onlineAgents);
      next.delete(userId);
      return { onlineAgents: next };
    }),

  setAgentTyping: (conversationId: string, userId: string, name: string) =>
    set((state) => {
      const key = `${conversationId}:${userId}`;
      const next = new Map(state.typingAgents);
      next.set(key, { conversationId, userId, name });
      return { typingAgents: next };
    }),

  clearAgentTyping: (conversationId: string, userId: string) =>
    set((state) => {
      const key = `${conversationId}:${userId}`;
      const next = new Map(state.typingAgents);
      next.delete(key);
      return { typingAgents: next };
    }),

  setCustomerTyping: (conversationId: string, customerId: string, name: string) =>
    set((state) => {
      const key = `${conversationId}:${customerId}`;
      const next = new Map(state.typingCustomers);
      next.set(key, { conversationId, customerId, name });
      return { typingCustomers: next };
    }),

  clearCustomerTyping: (conversationId: string, customerId: string) =>
    set((state) => {
      const key = `${conversationId}:${customerId}`;
      const next = new Map(state.typingCustomers);
      next.delete(key);
      return { typingCustomers: next };
    }),

  reset: () =>
    set({
      onlineAgents: new Map(),
      typingAgents: new Map(),
      typingCustomers: new Map(),
    }),
}));
